import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { RegionSelectorService } from '@dashboard/app/services/region-selector.service';
import { OpenIdConfiguration } from '@go-modules/interfaces/auth-providers';
import { NgxAuthService } from '@go-modules/services/auth/auth.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

export interface LoginEvent {
	username: string;
	password: string;
}

@Component({
	selector: 'ngx-login',
	templateUrl: './login.component.html',
	styleUrls: [
		'./login.component.scss',
		'./login-sm.component.scss'
	],
	standalone: false
})
export class LoginComponent implements OnChanges, OnInit, OnDestroy {
	@Input() public isDashboard: boolean = false;
	@Output() public onSignUp: EventEmitter<void> = new EventEmitter();
	@Output() public onForgotPassword: EventEmitter<void> = new EventEmitter();
	@Output() public onLogin: EventEmitter<LoginEvent> = new EventEmitter();
	@Output() public onLoginWithSso: EventEmitter<string> = new EventEmitter();

	@Input() public errorMessage: string;
	@Input() public disabledButton: boolean = false;
	@Input() public email: string|null;

	public form: UntypedFormGroup;
	public username = new UntypedFormControl('', [Validators.required, Validators.email, Validators.maxLength(255)]);
	public password = new UntypedFormControl('', Validators.required);

	public openIdProviders: OpenIdConfiguration[] = [];
	public preferredSsoProvider?: OpenIdConfiguration;
	public currentRegionLabel: string | null = null;

	private destroy$ = new Subject<void>();
	private previousShowState = false;

	constructor (
		fb: UntypedFormBuilder,
		public authService: NgxAuthService,
		private regionSelectorService: RegionSelectorService
	) {
		this.form = fb.group({
			username: this.username,
			password: this.password
		});
	}

	public ngOnInit () {
		if (this.isDashboard) {
			this.updateRegionLabel();

			this.regionSelectorService.showRegionSelector$
				.pipe(
					filter((show) => !show && this.previousShowState),
					takeUntil(this.destroy$)
				)
				.subscribe(() => {
					this.updateRegionLabel();
				});

			this.regionSelectorService.showRegionSelector$
				.pipe(takeUntil(this.destroy$))
				.subscribe((show) => {
					this.previousShowState = show;
				});
		}
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private updateRegionLabel (): void {
		const regionInfo = this.regionSelectorService.getCurrentRegionInfo();
		regionInfo.currentRegionLabel$.subscribe((label) => {
			this.currentRegionLabel = label;
		});
	}

	public ngOnChanges (changes: SimpleChanges) {
		if (changes.email) {
			this.form.controls.username.setValue(this.email);
		}
	}

	public login () {
		this.form.markAllAsTouched();
		if (this.form.invalid) {
			return;
		}

		this.onLogin.emit(this.form.value);
	}

	public loginWithProvider (providerName: string) {
		this.onLoginWithSso.emit(providerName);
	}

	public openRegionSelector () {
		this.regionSelectorService.openRegionSelector();
	}
}
